import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import PageNav from '../components/PageNav'

import { ApiError, apiFetch } from '../lib/api'

// Reference data — facilities, physicians and technicians.
//
// Behavioural specification: `AdminPage.aspx` and the screens it links to
// (`AddFacility.aspx`, `AddPhysician.aspx`, `AddReadingPhysician.aspx`,
// `UpdateReferringPhysician.aspx`, `UpdateReadingPhysician.aspx`) plus its own
// technician grid. The legacy spreads these across separate pages that each
// navigate away and back via a `SENDER` query parameter; here they are one page
// with a tab per kind, because the only reason they were separate was that a
// WebForm is one form.
//
// `AddPhysician.aspx` is misnamed in the legacy — it creates a *referring*
// physician. The tabs use the accurate names.
//
// Editing follows the legacy's own coverage: referring physicians
// (`UpdateReferringPhysician.aspx`), reading physicians
// (`UpdateReadingPhysician.aspx`) and technicians (the in-place grid edit on
// `AdminPage.aspx`) are editable; facilities have no edit screen anywhere in the
// legacy, so they stay add-only. Each form offers exactly the fields its screen
// puts on the page — the reading-physician editor has no Passcode box because
// `Physician.UpdatePhysician` sends no `@Passcode`.
//
// None of those pages contains a `HasPermission` call: any signed-in user can
// add or edit a facility or a physician. That is reproduced rather than
// tightened, for the same reason as the assignment screen — inventing a gate
// would lock out users the legacy allows.

type Kind = 'facilities' | 'referring-physicians' | 'reading-physicians' | 'technicians'

interface Row {
  id: number
  name: string
  fax?: string | null
  description?: string | null
  signature?: string | null
  lname?: string | null
  fname?: string | null
  facilityid?: number | null
  networkprinterid?: number | null
  networkprinterid2?: number | null
  [k: string]: unknown
}

interface Named {
  id: number
  name: string
}

// `singular` is spelled out rather than derived. Stripping a trailing 's'
// turns "Facilities" into "Add Facilitie", which is what the button said.
const TABS: { key: Kind; label: string; singular: string; legacy: string }[] = [
  { key: 'facilities', label: 'Facilities', singular: 'Facility', legacy: 'AddFacility.aspx' },
  {
    key: 'referring-physicians', label: 'Referring Physicians',
    singular: 'Referring Physician', legacy: 'AddPhysician.aspx',
  },
  {
    key: 'reading-physicians', label: 'Reading Physicians',
    singular: 'Reading Physician', legacy: 'AddReadingPhysician.aspx',
  },
  { key: 'technicians', label: 'Technicians', singular: 'Technician', legacy: '' },
]

const TAB_KEYS = new Set<Kind>(TABS.map((t) => t.key))

// Which tabs offer an inline edit, and what the legacy screen behind it is.
const EDITABLE: Partial<Record<Kind, string>> = {
  'referring-physicians': 'UpdateReferringPhysician.aspx',
  'reading-physicians': 'UpdateReadingPhysician.aspx',
  technicians: 'AdminPage.aspx',
}

// The response key differs per endpoint; it is the sole key of the object.
function rowsOf(payload: Record<string, Row[]>): Row[] {
  const key = Object.keys(payload)[0]
  return payload[key] ?? []
}

export default function ReferenceDataPage() {
  // `?sender=` and `?tab=`, the legacy's own round trip.
  // `StudyInformation.aspx:740` sends
  // `AddPhysician.aspx?SENDER=StudyInformation.aspx?STUDYID=<id>` so that adding
  // a referring physician mid-assignment returns to the study being assigned.
  // The tab is carried too, because the legacy sent the user to the one page
  // that added the kind they wanted; one page with tabs has to be told which.
  const [params] = useSearchParams()
  const [tab, setTab] = useState<Kind>(
    TAB_KEYS.has(params.get('tab') as Kind) ? (params.get('tab') as Kind) : 'facilities',
  )
  const [rows, setRows] = useState<Row[]>([])
  const [filter, setFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  // The two dropdowns `UpdateReadingPhysician.aspx` binds in its Edit command:
  // `FacilityCollection.GetReadingFacilityCollection` and
  // `NetworkPrinterCollection.GetNetworkPrinterCollection`. Fetched once, and
  // only for the tab that needs them.
  const [facilities, setFacilities] = useState<Named[]>([])
  const [printers, setPrinters] = useState<Named[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setRows(rowsOf(await apiFetch<Record<string, Row[]>>(`/api/v1/reference/${tab}`)))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [tab])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (tab !== 'reading-physicians' || facilities.length || printers.length) return
    void (async () => {
      try {
        const [f, p] = await Promise.all([
          apiFetch<{ facilities: Named[] }>('/api/v1/reference/facilities'),
          apiFetch<{ network_printers: Named[] }>('/api/v1/reference/network-printers'),
        ])
        setFacilities(f.facilities)
        setPrinters(p.network_printers)
      } catch (e) {
        setError(e instanceof ApiError ? e.message : String(e))
      }
    })()
  }, [tab, facilities.length, printers.length])

  const nameOf = (list: Named[], id: unknown) =>
    list.find((o) => o.id === Number(id))?.name ?? (id ? String(id) : '')

  const shown = filter.trim()
    ? rows.filter((r) => (r.name ?? '').toLowerCase().includes(filter.trim().toLowerCase()))
    : rows

  return (
    <main className="page page-wide">
      {/* `AddPhysician.aspx:17-20` — "Back  Home  Log Off". `AdminPage.aspx:17`
          is "Home  Log Off"; this page stands for both, because it merges
          AdminPage with the four child screens that carry the Back. Back draws
          only when a `?sender=` says where from — the legacy's own condition,
          and `PageNav` is what validates it. */}
      <PageNav strip="reference" />
      <h1>Reference Data</h1>

      {error && <p className="banner banner-error">{error}</p>}
      {notice && <p className="banner banner-ok">{notice}</p>}

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={t.key === tab ? 'tab tab-on' : 'tab'}
            onClick={() => {
              setTab(t.key)
              setAdding(false)
              setNotice(null)
              setFilter('')
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="filter-row">
        <label>
          Find
          <input
            value={filter}
            placeholder="name contains…"
            onChange={(e) => setFilter(e.target.value)}
          />
        </label>
        <button type="button" onClick={() => setAdding((a) => !a)}>
          {adding ? 'Cancel' : `Add ${TABS.find((t) => t.key === tab)?.singular}`}
        </button>
        <span className="muted">
          {loading ? 'Loading…' : `${shown.length} of ${rows.length}`}
        </span>
      </div>

      {adding && (
        <AddForm
          kind={tab}
          onDone={(msg) => {
            setAdding(false)
            setNotice(msg)
            void load()
          }}
          onError={setError}
        />
      )}

      <div className="grid-scroll">
        <table className="grid">
          <thead>
            <tr>
              <th>Id</th>
              <th>Name</th>
              {tab === 'facilities' && <th>Description</th>}
              {(tab === 'reading-physicians' || tab === 'technicians') && <th>Signature</th>}
              {tab === 'reading-physicians' && <th>Facility</th>}
              {tab === 'reading-physicians' && <th>Printer</th>}
              {tab === 'reading-physicians' && <th>Printer 2</th>}
              {tab !== 'technicians' && <th>Fax</th>}
              {EDITABLE[tab] && <th />}
            </tr>
          </thead>
          <tbody>
            {shown.slice(0, 500).map((r) => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td>{r.name}</td>
                {tab === 'facilities' && <td>{r.description}</td>}
                {(tab === 'reading-physicians' || tab === 'technicians') && (
                  <td>{r.signature}</td>
                )}
                {tab === 'reading-physicians' && <td>{nameOf(facilities, r.facilityid)}</td>}
                {tab === 'reading-physicians' && (
                  <td>{nameOf(printers, r.networkprinterid)}</td>
                )}
                {tab === 'reading-physicians' && (
                  <td>{nameOf(printers, r.networkprinterid2)}</td>
                )}
                {tab !== 'technicians' && <td>{fmtFax(r.fax)}</td>}
                {EDITABLE[tab] && (
                  <td>
                    <EditRecord
                      kind={tab}
                      row={r}
                      facilities={facilities}
                      printers={printers}
                      onDone={(msg) => {
                        setNotice(msg)
                        void load()
                      }}
                      onError={setError}
                    />
                  </td>
                )}
              </tr>
            ))}
            {!loading && shown.length === 0 && (
              <tr>
                <td colSpan={9} className="muted">
                  Nothing matches.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {shown.length > 500 && (
        <p className="muted">Showing the first 500 of {shown.length}. Narrow the filter.</p>
      )}
    </main>
  )
}

// Stored as ten bare digits (`Mindcreek.FW.Types.Phone`); displayed grouped.
function fmtFax(fax: unknown) {
  const s = String(fax ?? '')
  if (s.length !== 10) return s
  return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}`
}

function AddForm({
  kind,
  onDone,
  onError,
}: {
  kind: Kind
  onDone: (msg: string) => void
  onError: (msg: string) => void
}) {
  const [v, setV] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const set = (k: string) => (e: { target: { value: string } }) =>
    setV((prev) => ({ ...prev, [k]: e.target.value }))

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      const body: Record<string, unknown> =
        kind === 'facilities'
          ? { name: v.name, description: v.description, fax: v.fax || null }
          : kind === 'referring-physicians'
            ? { name: v.name, fax: v.fax || null }
            : kind === 'reading-physicians'
              ? {
                  last_name: v.last_name,
                  first_name: v.first_name,
                  signature: v.signature,
                  facility_id: Number(v.facility_id || 0),
                  fax: v.fax || null,
                }
              : { last_name: v.last_name, first_name: v.first_name, signature: v.signature }

      const created = await apiFetch<{ id: number }>(`/api/v1/reference/${kind}`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      onDone(`Created #${created.id}.`)
      setV({})
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="filters" onSubmit={submit}>
      <legend>New</legend>
      <div className="filter-row">
        {kind === 'facilities' && (
          <>
            <label>
              Name<input required value={v.name ?? ''} onChange={set('name')} />
            </label>
            <label>
              Description
              <input value={v.description ?? ''} onChange={set('description')} />
            </label>
          </>
        )}
        {kind === 'referring-physicians' && (
          <label>
            Name<input required value={v.name ?? ''} onChange={set('name')} />
          </label>
        )}
        {(kind === 'reading-physicians' || kind === 'technicians') && (
          <>
            <label>
              Last name
              <input required value={v.last_name ?? ''} onChange={set('last_name')} />
            </label>
            <label>
              First name
              <input value={v.first_name ?? ''} onChange={set('first_name')} />
            </label>
            <label>
              Signature
              <input
                value={v.signature ?? ''}
                onChange={set('signature')}
                placeholder="printed on the report"
              />
            </label>
          </>
        )}
        {kind === 'reading-physicians' && (
          <label>
            Facility id
            <input value={v.facility_id ?? ''} onChange={set('facility_id')} />
          </label>
        )}
        {kind !== 'technicians' && (
          <label>
            Fax<input value={v.fax ?? ''} onChange={set('fax')} placeholder="10 digits" />
          </label>
        )}
        <button type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  )
}

// The three records the legacy edits in place, each offering exactly the fields
// its own screen offers:
//
// * referring physician — `UpdateReferringPhysician.aspx`: name and fax.
// * reading physician — `UpdateReadingPhysician.aspx` `btnReadPhyUpdate_Click`:
//   FName, LName, Signature, Fax, Facility, and the two network printers.
//   `SignatureFile` is assigned to itself there and `Passcode` is not on the
//   form, so neither appears here.
// * technician — `AdminPage.aspx` `DataGrid1_UpdateCommand`: FName, LName and
//   Signature, read out of grid cells 1, 2 and 3.
//
// Only changed fields are sent; the server diffs again and writes nothing when
// nothing moved. `''` on a printer or facility select is the legacy's "Select
// a …" entry, which it stores as 0 — "none selected", not "unset".
function EditRecord({
  kind,
  row,
  facilities,
  printers,
  onDone,
  onError,
}: {
  kind: Kind
  row: Row
  facilities: Named[]
  printers: Named[]
  onDone: (msg: string) => void
  onError: (msg: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [v, setV] = useState<Record<string, string>>({})

  function begin() {
    setV({
      name: row.name ?? '',
      last_name: String(row.lname ?? ''),
      first_name: String(row.fname ?? ''),
      signature: String(row.signature ?? ''),
      fax: String(row.fax ?? ''),
      facility_id: String(row.facilityid ?? ''),
      network_printer_id: String(row.networkprinterid ?? ''),
      network_printer_id2: String(row.networkprinterid2 ?? ''),
    })
    setOpen(true)
  }

  const set = (k: string) => (e: { target: { value: string } }) =>
    setV((prev) => ({ ...prev, [k]: e.target.value }))

  async function save() {
    setBusy(true)
    try {
      const body: Record<string, unknown> =
        kind === 'referring-physicians'
          ? { name: v.name, fax: v.fax || null }
          : kind === 'technicians'
            ? {
                last_name: v.last_name,
                first_name: v.first_name,
                signature: v.signature,
              }
            : {
                last_name: v.last_name,
                first_name: v.first_name,
                signature: v.signature,
                fax: v.fax || null,
                facility_id: Number(v.facility_id || 0),
                network_printer_id: Number(v.network_printer_id || 0),
                network_printer_id2: Number(v.network_printer_id2 || 0),
              }

      const r = await apiFetch<{ saved: boolean; changed: Record<string, unknown> }>(
        `/api/v1/reference/${kind}/${row.id}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      )
      onDone(
        r.saved ? `Updated #${row.id}: ${Object.keys(r.changed).join(', ')}.` : 'No change.',
      )
      setOpen(false)
    } catch (e) {
      onError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button type="button" className="open" onClick={begin}>
        Edit
      </button>
    )
  }

  return (
    <span className="inline-edit">
      {kind === 'referring-physicians' ? (
        <input value={v.name ?? ''} onChange={set('name')} placeholder="name" />
      ) : (
        <>
          <input value={v.last_name ?? ''} onChange={set('last_name')} placeholder="last name" />
          <input value={v.first_name ?? ''} onChange={set('first_name')} placeholder="first name" />
          <input value={v.signature ?? ''} onChange={set('signature')} placeholder="signature" />
        </>
      )}
      {kind === 'reading-physicians' && (
        <>
          <select value={v.facility_id ?? ''} onChange={set('facility_id')}>
            <option value="">Select a Facility</option>
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <select value={v.network_printer_id ?? ''} onChange={set('network_printer_id')}>
            <option value="">Select a Printer</option>
            {printers.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select value={v.network_printer_id2 ?? ''} onChange={set('network_printer_id2')}>
            <option value="">Select a Printer</option>
            {printers.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </>
      )}
      {kind !== 'technicians' && (
        <input value={v.fax ?? ''} onChange={set('fax')} placeholder="10 digits" />
      )}
      <button type="button" disabled={busy} onClick={save}>
        {busy ? 'Saving…' : 'Save'}
      </button>
      <button type="button" onClick={() => setOpen(false)}>
        Cancel
      </button>
    </span>
  )
}
